import JsBarcode from "jsbarcode";

/**
 * Generates a barcode data URL for the given text
 * @param text The text to encode in the barcode
 * @param options Optional configuration options for the barcode
 * @returns A Promise that resolves to a data URL containing the barcode image
 */
export const generateBarcodeDataURL = (
  text: string,
  options: {
    format?: string;
    width?: number;
    height?: number;
    displayValue?: boolean;
    fontSize?: number;
    textMargin?: number;
    background?: string;
    lineColor?: string;
    margin?: number;
  } = {}
): Promise<string> => {
  return new Promise((resolve, reject) => {
    try {
      // Create a canvas element
      const canvas = document.createElement("canvas");

      // Set default options
      const defaultOptions = {
        format: "CODE128", // Most common 1D barcode format that supports all characters
        width: 2, // Width of the bars
        height: 50, // Height of the barcode
        displayValue: true, // Show the text below the barcode
        fontSize: 12, // Font size of the text
        textMargin: 2, // Margin between the barcode and the text
        background: "#ffffff", // Background color
        lineColor: "#000000", // Bar color
        margin: 5, // Margin around the barcode
        ...options, // Override with any provided options
      };

      // Validate text input
      if (!text || text.trim() === "") {
        reject(new Error("Barcode text cannot be empty"));
        return;
      }

      // Generate the barcode on the canvas
      JsBarcode(canvas, text, defaultOptions);

      // Convert the canvas to a data URL with high quality
      const dataURL = canvas.toDataURL("image/png", 1.0);

      // Log barcode generation details for debugging
      console.log("Generated barcode:", {
        text,
        options: defaultOptions,
        canvasSize: { width: canvas.width, height: canvas.height },
      });

      resolve(dataURL);
    } catch (error) {
      console.error("Failed to generate barcode:", error);
      reject(error);
    }
  });
};

/**
 * Formats a ticket number for CODE128 barcode
 * @param ticketNumber The ticket number to convert
 * @returns A formatted string for barcode generation
 */
export const formatTicketForBarcode = (
  ticketNumber: number | string
): string => {
  // Convert to string if it's a number
  let numStr = ticketNumber.toString();

  // Remove any non-numeric characters
  numStr = numStr.replace(/\D/g, "");

  // Return the cleaned ticket number
  return numStr;
};

/**
 * Generates a barcode specifically for repair tickets
 * @param repairId The repair ID or ticket number to encode
 * @returns A Promise that resolves to a data URL containing the barcode image
 */
export const generateRepairBarcode = (
  ticketNumber: string,
  isForTicket: boolean = false
): Promise<string> => {
  // Format the ticket number for CODE128 barcode
  const formattedTicket = formatTicketForBarcode(ticketNumber);

  console.log("Generating CODE128 barcode for ticket number:", ticketNumber);
  console.log("Formatted ticket number:", formattedTicket);

  // For repair tickets, we want a taller barcode that's easier to scan
  if (isForTicket) {
    return generateBarcodeDataURL(formattedTicket, {
      format: "CODE128", // Use CODE128 which accepts any text
      width: 2, // Thicker for better readability
      height: 70, // Taller for better scanning
      displayValue: true, // Show the number below the barcode
      fontSize: 10, // Slightly larger font for better readability
      margin: 5, // Small margin for better scanning
      textMargin: 2, // Small text margin
    });
  }

  // For standalone printing, we want a high-quality, readable barcode
  return generateBarcodeDataURL(formattedTicket, {
    format: "CODE128", // Use CODE128 which accepts any text
    width: 3, // Increased bar width for better scanning (was 2.5)
    height: 80, // Optimized height for better scanning (was 120)
    displayValue: false, // Disable built-in text to avoid conflicts (like sticker)
    margin: 8, // Sufficient margin for safety (was 10)
    background: "#ffffff",
    lineColor: "#000000",
  });
};

/**
 * Generates a barcode specifically optimized for small stickers (40mm x 20mm)
 * @param ticketNumber The ticket number to encode
 * @returns A Promise that resolves to a data URL containing the barcode image
 */
export const generateStickerBarcode = (
  ticketNumber: string
): Promise<string> => {
  // Format the ticket number for CODE128 barcode
  const formattedTicket = formatTicketForBarcode(ticketNumber);

  console.log("Generating sticker barcode for ticket number:", ticketNumber);
  console.log("Formatted ticket number:", formattedTicket);

  // Optimized settings for 40mm x 20mm stickers - simplified for better printer compatibility
  return generateBarcodeDataURL(formattedTicket, {
    format: "CODE128",
    width: 2, // Increased bar width for better scanning and printer compatibility
    height: 30, // Increased height for better visibility
    displayValue: false, // Disable built-in text to avoid conflicts
    margin: 3, // Increased margin for safety
    background: "#ffffff",
    lineColor: "#000000",
  });
};
